import { useState, useEffect, useRef, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronsUpDown, ChevronUp, ChevronDown, Copy, BookOpen, Type, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import type { ContentItem } from "@/types/content";
import type { MediaController } from "../adapters/types";
import type { RecordingState } from "../MediaContentViewer";
import { useMediaTranscript, useTranscriptStatus, useGenerateTranscript } from "@/hooks/useMediaTranscript";
import { useMediaChapters, useChaptersStatus, useGenerateChapters, type MediaChapter } from "@/hooks/useMediaChapters";

interface MediaInsightsTabsProps {
  content: ContentItem;
  controller: MediaController | null;
  isPlayerCollapsed?: boolean;
  onTogglePlayer?: () => void;
  isNewRecording?: boolean;
  recordingState?: RecordingState;
}

// Helper to format seconds as MM:SS or HH:MM:SS
function formatTimestamp(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor(seconds % 3600 / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

// Shimmer text component
function ShimmerText({ children }: { children: React.ReactNode }) {
  return <span className="shimmer-text">{children}</span>;
}

export function MediaInsightsTabs({
  content,
  controller,
  isPlayerCollapsed = false,
  onTogglePlayer,
  isNewRecording = false,
  recordingState = "idle"
}: MediaInsightsTabsProps) {
  const [activeTab, setActiveTab] = useState("chapters");
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);
  const [activeTranscriptIndex, setActiveTranscriptIndex] = useState<number>(-1);
  const transcriptRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Fetch transcript data
  const {
    data: transcripts,
    isLoading: isLoadingTranscripts
  } = useMediaTranscript(content.id);
  const {
    data: transcriptStatus,
    isLoading: isLoadingStatus
  } = useTranscriptStatus(content.id);
  const generateTranscript = useGenerateTranscript();

  // Fetch chapters data
  const {
    data: chapters,
    isLoading: isLoadingChapters
  } = useMediaChapters(content.id);
  const {
    data: chaptersStatus,
    isLoading: isLoadingChaptersStatus
  } = useChaptersStatus(content.id);
  const generateChapters = useGenerateChapters();

  // Check if content type supports transcription (now includes YouTube)
  const supportsTranscription = ['audio', 'video', 'recording', 'youtube'].includes(content.type);

  // Full transcript text for copy
  const transcriptText = transcripts?.map(s => s.text).join('\n') || '';

  // Auto-scroll to active transcript segment
  useEffect(() => {
    if (!autoScrollEnabled || activeTranscriptIndex < 0 || activeTab !== 'transcript') return;
    const activeRef = transcriptRefs.current[activeTranscriptIndex];
    if (activeRef) {
      activeRef.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeTranscriptIndex, autoScrollEnabled, activeTab]);

  // Subscribe to time updates for transcript highlighting
  useEffect(() => {
    if (!controller || !transcripts || transcripts.length === 0) return;
    const unsubscribe = controller.onTimeUpdate(time => {
      // Find segment containing current time
      const index = transcripts.findIndex(seg => time >= seg.start_seconds && time < seg.end_seconds);
      if (index !== -1 && index !== activeTranscriptIndex) {
        setActiveTranscriptIndex(index);
      }
    });
    return unsubscribe;
  }, [controller, transcripts, activeTranscriptIndex]);
  const handleCopyTranscript = async () => {
    if (!transcriptText) {
      toast.error("No transcript available to copy");
      return;
    }
    try {
      await navigator.clipboard.writeText(transcriptText);
      toast.success("Transcript copied to clipboard");
    } catch (err) {
      toast.error("Failed to copy transcript");
    }
  };
  const toggleAutoScroll = () => {
    setAutoScrollEnabled(prev => !prev);
    toast.success(autoScrollEnabled ? "Auto scroll disabled" : "Auto scroll enabled");
  };
  const handleChapterClick = (chapter: MediaChapter) => {
    if (controller) {
      controller.seek(chapter.start_seconds);
      controller.play();
    }
  };
  const handleTranscriptClick = useCallback((startSeconds: number) => {
    if (controller) {
      controller.seek(startSeconds);
      controller.play();
    }
  }, [controller]);
  const handleGenerateTranscript = async () => {
    try {
      await generateTranscript.mutateAsync(content.id);
      toast.success("Transcript generation started");
    } catch (error: any) {
      toast.error(error.message || "Failed to generate transcript");
    }
  };
  const handleGenerateChapters = async () => {
    try {
      await generateChapters.mutateAsync(content.id);
      toast.success("Chapters generated successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to generate chapters");
    }
  };

  // Render chapters content based on status
  const renderChaptersContent = () => {
    // New recording - show dynamic messages based on recording state
    if (isNewRecording) {
      // Recording in progress
      if (recordingState === "recording") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Recording in progress...</ShimmerText>
            </p>
          </div>;
      }
      // Recording paused
      if (recordingState === "paused") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Recording paused...</ShimmerText>
            </p>
          </div>;
      }
      // Recording stopped (waiting to save)
      if (recordingState === "stopped") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Generating Chapters</ShimmerText>
            </p>
          </div>;
      }
      // Idle state - show prompt to start recording
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="mb-2 text-base font-medium">Start recording to generate chapters</p>
        </div>;
    }

    // Content type doesn't support chapters (needs transcript first)
    if (!supportsTranscription) {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <BookOpen className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Chapters are not available for this content type.
          </p>
        </div>;
    }

    // Loading status
    if (isLoadingChaptersStatus || isLoadingChapters) {
      return <div className="flex flex-col gap-4 p-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="space-y-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-12 w-full" />
            </div>)}
        </div>;
    }

    // Processing state
    if (chaptersStatus === 'processing') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-primary" />
          <p className="mb-2 text-sm font-medium">Generating chapters...</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            AI is analyzing the transcript to identify topic segments.
          </p>
        </div>;
    }

    // Failed state
    if (chaptersStatus === 'failed') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <BookOpen className="mb-4 h-12 w-12 text-destructive/50" />
          <p className="mb-2 text-sm font-medium text-destructive">
            Chapter generation failed
          </p>
          <button onClick={handleGenerateChapters} disabled={generateChapters.isPending} className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {generateChapters.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Try Again
          </button>
        </div>;
    }

    // Content too short for chapters
    if (chaptersStatus === 'not_needed') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <BookOpen className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="mb-2 text-sm font-medium">Content too short for chapters</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Chapters are generated for content longer than 1 minute.
          </p>
        </div>;
    }

    // No transcript yet - chapters depend on transcript
    if (transcriptStatus !== 'ready') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Sparkles className="mb-4 h-12 w-12 text-primary/50" />
          <p className="mb-2 text-sm font-medium">Chapters will be generated automatically</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            Once the transcript is ready, AI will analyze it to create chapters.
          </p>
        </div>;
    }

    // No chapters yet (pending state) - transcript is ready but chapters not generated
    if (!chapters || chapters.length === 0) {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Sparkles className="mb-4 h-12 w-12 text-primary/50" />
          <p className="mb-2 text-sm font-medium">No chapters available</p>
          <p className="mb-4 max-w-xs text-xs text-muted-foreground">
            Generate AI-powered chapters to navigate through this content easily.
          </p>
          <button onClick={handleGenerateChapters} disabled={generateChapters.isPending} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {generateChapters.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate Chapters
          </button>
        </div>;
    }

    // Display chapters
    return <div className="flex flex-col">
        {chapters.map(chapter => <button key={chapter.id} type="button" onClick={() => handleChapterClick(chapter)} className="group cursor-pointer rounded-2xl border border-transparent p-3 pb-4 text-left transition-colors hover:bg-primary/5">
            {/* Timestamp badge at top */}
            <div className="mb-2 flex items-start">
              <span className="inline-flex cursor-pointer items-center rounded-md border border-transparent px-2.5 py-0.5 text-xs font-medium text-primary/80 transition-colors hover:text-foreground bg-card">
                {formatTimestamp(chapter.start_seconds)}
              </span>
            </div>
            {/* Title */}
            <h3 className="mb-2 line-clamp-1 text-lg text-current">
              {chapter.title}
            </h3>
            {/* Summary */}
            {chapter.summary && <p className="text-sm leading-relaxed text-muted-foreground">
                {chapter.summary}
              </p>}
          </button>)}
      </div>;
  };

  // Render transcript content based on status
  const renderTranscriptContent = () => {
    // New recording - show dynamic messages based on recording state
    if (isNewRecording) {
      // Recording in progress
      if (recordingState === "recording") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Recording in progress...</ShimmerText>
            </p>
          </div>;
      }
      // Recording paused
      if (recordingState === "paused") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Recording paused...</ShimmerText>
            </p>
          </div>;
      }
      // Recording stopped (waiting to save)
      if (recordingState === "stopped") {
        return <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="mb-2 text-base font-medium">
              <ShimmerText>Generating Transcripts</ShimmerText>
            </p>
          </div>;
      }
      // Idle state - show prompt to start recording
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="mb-2 text-base font-medium">Start recording to generate transcript</p>
        </div>;
    }

    // Content type doesn't support transcription
    if (!supportsTranscription) {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Type className="mb-4 h-12 w-12 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            Transcription is not available for this content type.
          </p>
        </div>;
    }

    // Loading status
    if (isLoadingStatus || isLoadingTranscripts) {
      return <div className="flex flex-col gap-4 p-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="space-y-2">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-12 w-full" />
            </div>)}
        </div>;
    }

    // Processing state
    if (transcriptStatus === 'processing') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-primary" />
          <p className="mb-2 text-sm font-medium">Generating transcript...</p>
          <p className="max-w-xs text-xs text-muted-foreground">
            This may take a few moments depending on the length of the media.
          </p>
        </div>;
    }

    // Failed state
    if (transcriptStatus === 'failed') {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Type className="mb-4 h-12 w-12 text-destructive/50" />
          <p className="mb-2 text-sm font-medium text-destructive">
            Transcription failed
          </p>
          <button onClick={handleGenerateTranscript} disabled={generateTranscript.isPending} className="mt-2 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {generateTranscript.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Try Again
          </button>
        </div>;
    }

    // No transcript yet (pending state)
    if (!transcripts || transcripts.length === 0) {
      return <div className="flex flex-col items-center justify-center py-12 text-center">
          <Sparkles className="mb-4 h-12 w-12 text-primary/50" />
          <p className="mb-2 text-sm font-medium">No transcript available</p>
          <p className="mb-4 max-w-xs text-xs text-muted-foreground">
            Generate a transcript to see the spoken content of this media file.
          </p>
          <button onClick={handleGenerateTranscript} disabled={generateTranscript.isPending} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {generateTranscript.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate Transcript
          </button>
        </div>;
    }

    // Display transcript segments
    return <div className="flex flex-col">
        {transcripts.map((segment, index) => <button key={segment.id} ref={el => {
        transcriptRefs.current[index] = el;
      }} type="button" onClick={() => handleTranscriptClick(segment.start_seconds)} className={`group cursor-pointer rounded-2xl border border-transparent p-3 pb-4 text-left transition-colors hover:bg-primary/5 ${index === activeTranscriptIndex ? 'bg-primary/10 border-primary/20' : ''}`}>
            {/* Timestamp badge at top */}
            <div className="mb-2 flex items-start">
              <span className={`inline-flex cursor-pointer items-center rounded-md border border-transparent px-2.5 py-0.5 text-xs font-medium transition-colors hover:bg-foreground/10 hover:text-foreground ${index === activeTranscriptIndex ? 'bg-primary/20 text-primary' : 'bg-neutral-100 text-primary/80 dark:bg-neutral-800'}`}>
                {formatTimestamp(segment.start_seconds)}
              </span>
            </div>
            {/* Transcript text */}
            <p className={`text-sm leading-relaxed ${index === activeTranscriptIndex ? 'text-primary font-medium' : 'text-primary/95'}`}>
              {segment.text}
            </p>
          </button>)}
      </div>;
  };
  return <Tabs value={activeTab} onValueChange={setActiveTab} className="flex h-full flex-col overflow-hidden">
      {/* Header row with tabs and control buttons */}
      <div className="relative flex flex-row items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <TabsList className="relative mt-4 h-auto w-fit items-center justify-start overflow-x-auto rounded-2xl border border-primary/10 bg-white px-[3px] dark:border-primary/5 dark:bg-neutral-800/50">
            <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide py-[2px]">
              <TabsTrigger value="chapters" className="flex w-full items-center gap-2 rounded-lg border-transparent px-3 py-1.5 font-normal text-primary/80 hover:bg-primary/5 hover:text-primary data-[state=active]:border-transparent data-[state=active]:bg-primary/5 data-[state=active]:text-primary dark:border-primary/10 dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-primary/10">
                <div className="flex items-center gap-2">
                  {activeTab === "chapters" ? <div className="mx-1 h-2 w-2 flex-shrink-0 rounded-full bg-correct" /> : <BookOpen className="h-4 w-4" />}
                  <span className="hidden sm:inline">Chapters</span>
                </div>
              </TabsTrigger>
              <TabsTrigger value="transcript" className="flex w-full items-center gap-2 rounded-lg border-transparent px-3 py-1.5 font-normal text-primary/80 hover:bg-primary/5 hover:text-primary data-[state=active]:border-transparent data-[state=active]:bg-primary/5 data-[state=active]:text-primary dark:border-primary/10 dark:data-[state=active]:border-transparent dark:data-[state=active]:bg-primary/10">
                <div className="flex items-center gap-2">
                  {activeTab === "transcript" ? <div className="mx-1 h-2 w-2 flex-shrink-0 rounded-full bg-correct" /> : <Type className="h-4 w-4" />}
                  <span className="hidden sm:inline">Transcripts</span>
                </div>
              </TabsTrigger>
            </div>
          </TabsList>
        </div>

        {/* Control buttons */}
        <div className="ml-auto mt-[15px] flex items-center gap-2">
          {/* Copy button - only on Transcripts tab when transcript exists */}
          {activeTab === "transcript" && transcripts && transcripts.length > 0 && <button type="button" onClick={handleCopyTranscript} className="inline-flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-2xl border bg-white px-3 text-sm font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-colors duration-200 hover:bg-neutral-100 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:border-primary/10 dark:bg-neutral-800/50 dark:hover:bg-neutral-800" title="Copy transcript">
              <Copy className="h-4 w-4" />
            </button>}

          {/* Auto Scroll button */}
          <div className="flex h-full items-center rounded-2xl border border-primary/10 bg-white p-[3px] dark:border-primary/5 dark:bg-neutral-800/50">
            <button type="button" onClick={toggleAutoScroll} className={`inline-flex h-full items-center justify-center gap-2 whitespace-nowrap rounded-[13px] border-none px-3 py-1.5 text-sm font-medium shadow-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 ${autoScrollEnabled ? "bg-primary/5 text-primary dark:bg-primary/10" : "bg-transparent text-primary/80"}`} title={autoScrollEnabled ? "Disable auto scroll" : "Enable auto scroll"}>
              <ChevronsUpDown className="h-4 w-4" />
              <span className="text-sm font-normal">Auto Scroll</span>
            </button>
          </div>

          {/* Collapse/Expand button - only show for video and youtube */}
          {(content.type === 'video' || content.type === 'youtube') && <button type="button" onClick={onTogglePlayer} className="inline-flex h-10 w-10 items-center justify-center whitespace-nowrap rounded-2xl border bg-white px-3 text-sm font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-colors duration-200 hover:bg-neutral-100 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 dark:border-primary/10 dark:bg-neutral-800/50 dark:hover:bg-neutral-800" title={isPlayerCollapsed ? "Expand player" : "Collapse player"}>
              {isPlayerCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </button>}
        </div>
      </div>

      <TabsContent value="chapters" className="mt-4 min-h-0 flex-1">
        <ScrollArea className="h-full w-full">
          {renderChaptersContent()}
        </ScrollArea>
      </TabsContent>

      <TabsContent value="transcript" className="mt-4 min-h-0 flex-1">
        <ScrollArea className="h-full w-full">
          {renderTranscriptContent()}
        </ScrollArea>
      </TabsContent>
    </Tabs>;
}